import { ReactQueryOptions, RouterInputs } from "@utils/trpc";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useData, useApi, useDataAuth } from "../../../runtime/provider";
import type { DataSession } from "../../../data/core/session";
import { userMeWireSchema } from "@libs/wire";

import { meQuery, meQueryKey, type UserMeQueryData } from "./queryFunctions";

export type { UserMeQueryData } from "./queryFunctions";

type QueryOptions = ReactQueryOptions["user"]["me"];

/**
 * Back-compat export. 26+ consumers (and `feedSwitcher.test.tsx` mocking
 * with a literal `["user", "me"]`) depend on this. Remains pinned to
 * `meQueryKey()` — the source of truth.
 */
export const USER_ME_QUERY_KEY = meQueryKey();

export const useMe = (
  // `refetchUserAfterUpdate` is unused at runtime but preserved in the
  // signature to avoid a typecheck cascade across 26+ call sites.
  opts: QueryOptions & { refetchUserAfterUpdate?: boolean } = {
    refetchUserAfterUpdate: true,
  },
) => {
  const data = useData();
  const { firebaseUser, status } = useDataAuth();
  // A Firebase user exists the instant signup completes, but the users row is
  // written by `user.auth`, which useAuth is still awaiting at that point.
  // Firing `me` before that resolves returned NOT_FOUND once per signup
  // (Sentry LISSEN-API-2J: 675 users, one event each). Hold until the auth
  // endpoint has answered; the durable cache still seeds data meanwhile.
  const dbUserPending =
    status === "firebase-auth-pending" || status === "db-user-pending";
  const query = useQuery({
    ...meQuery(data),
    staleTime: 1000 * 60 * 60, // 1 hour
    retry: 3,
    retryDelay: 1000,
    ...opts,
    enabled: !!firebaseUser && !dbUserPending && (opts.enabled ?? true),
  });

  const { data: user, refetch } = query;

  const isPremium =
    user?.plan?.type === "premium" ||
    user?.consumer?.subscription === "premium";

  const newUpdates = user?.newUpdates;

  return {
    isPremium,
    query,
    newUpdates,
    refetch,
  };
};

/**
 * Synchronous user lookup for non-hook contexts (usePlayerAds,
 * usePlayerStreamAnalytics, WelcomeOverlay's countryCode read). Falls back
 * to the SQLite query_cache row when the in-memory cache is cold so a true
 * cold start (app just launched, network not yet returned) still gives
 * downstream code a concrete user value rather than `undefined`. Uses the
 * same wire schema as the queryFn — a row that fails parse is purged on
 * read, mirroring the cold-mount initialData behaviour.
 */
export const getUser = (
  data: Pick<DataSession, "queryClient" | "cache">,
): UserMeQueryData | undefined => {
  const warm =
    data.queryClient.getQueryData<UserMeQueryData>(USER_ME_QUERY_KEY);
  if (warm) return warm;
  return data.cache.readQueryCacheSync<UserMeQueryData>(
    USER_ME_QUERY_KEY,
    userMeWireSchema,
  );
};

export const useUpdateUser = () => {
  const api = useApi();
  return useMutation({
    mutationFn: async (input: RouterInputs["user"]["update"]) => {
      const medata = await api.user.update.mutate(input);
      return medata;
    },
  });
};
